import React, { forwardRef, useEffect, useImperativeHandle, useState, useRef } from "react";
import LoadingSpinner from "../LoadingSpinner";
import Input from "../input/Input";
import InfoList from "../InfoList";
import { fuelRequestValidation } from "../validations/FuelRequestCreation";
import { formatFromJsonDate } from "../../dateConfig";
import { Droplet } from "react-feather";
import { FORMS, MODULES, STATUS_GROUPS, statusById } from "../../const";
import useUserCompany from "../../hooks/useUserCompany";
import { getFuelRequestJobOptions } from "../../api/FuelRequestApi";
import { StatusCellStyle } from "../renderer/StyleRenderer";
import { toast } from "react-toastify";
import { statusIdsByContexts } from "../../api/statusApi";
import SearchDropdown from "../input/SearchDropdown";

const FORM = FORMS.FUEL_REQUEST.HEADER.form;

const FuelRequestComponent = forwardRef(
    ({ errors, isLoading, currentValues, formMode, isRejecting }, ref) => {
        const isCreateMode = formMode === "create";
        const isApproveMode = formMode === "approve";
        const isPumperMode = formMode === "pumper";

        const userCompany = useUserCompany();
        const [company, setCompany] = useState(userCompany ? userCompany.comId : null);
        useEffect(() => { setCompany(userCompany ? userCompany.comId : null); }, [userCompany]);

        const [jobOptions, setJobOptions] = useState([]);
        const [frApprovalEnableList, setFrApprovalEnableList] = useState([]);

        const jobDropdownRef = useRef();

        useEffect(() => {
            if (!isCreateMode && currentValues) {
                setFuelRequestData({
                    ...currentValues,
                    frqApprovedAmount: formMode === "approve" && !currentValues.frqApprovedAmount
                        ? currentValues.frqRequestedAmount
                        : currentValues.frqApprovedAmount,

                    frqTopUpAmount: formMode === "pumper" && !currentValues.frqTopUpAmount
                        ? currentValues.frqApprovedAmount
                        : currentValues.frqTopUpAmount,
                });
            }
        }, [currentValues, formMode, isCreateMode]);

        useEffect(() => {
            if (!isCreateMode) return;
            (async () => {
                try {
                    const options = await getFuelRequestJobOptions();
                    setJobOptions(options);

                    const {
                        [STATUS_GROUPS.FUEL_REQUEST_APPROVAL_ENABLE]: enabledList = [],
                    } = await statusIdsByContexts([STATUS_GROUPS.FUEL_REQUEST_APPROVAL_ENABLE]);
                    setFrApprovalEnableList(enabledList);
                } catch (error) {
                    console.error("Failed to load jobs", error);
                }
            })();
        }, [isCreateMode]);

        const initialFormState = {
            frqJob: "",
            frqVehicle: "",
            frqVehicleNo: "",
            frqTransporter: "",
            frqTransporterName: "",
            frqTripDistance: "",
            frqCardNo: "",
            frqRequiredDate: "",
            frqRequestedAmount: "",
            frqDescription: "",
            frqRequestStatus: "Pending",
            frqTopUpStatus: "Pending",
            frqCompany: company,
        };

        const [fuelRequestData, setFuelRequestData] = useState(
            isCreateMode ? initialFormState : { ...currentValues }
        );

        useEffect(() => {
            if (isCreateMode) {
                setFuelRequestData((prev) => ({ ...prev, frqCompany: company }));
            }
        }, [company]);

        useEffect(() => {
            if (isRejecting) {
                setFuelRequestData((prev) => ({
                    ...prev,
                    ...(isApproveMode && { frqApproverRemarks: "" }),
                    ...(isPumperMode && { frqPumperRemarks: "" }),
                }));
            }
        }, [isRejecting, isApproveMode, isPumperMode]);

        const [currentErrors, setCurrentErrors] = useState({});
        useEffect(() => setCurrentErrors(errors), [errors]);

        useImperativeHandle(ref, () => ({
            getData: () => fuelRequestData,
            validateForm: () => fuelRequestValidation(fuelRequestData, formMode, isRejecting),
            resetForm: () => {
                setFuelRequestData({ ...initialFormState, frqCompany: company });
                setCurrentErrors({});
                jobDropdownRef.current?.reset();
            },
        }));

        const searchJobsAPI = async (searchParam) => {
            const text = typeof searchParam === "string" ? searchParam : searchParam?.searchText || "";
            if (!text || text.length < 3) return [];

            const response = await getFuelRequestJobOptions(text);

            return response
                .filter((j) => j.reqCode)
                .map((j) => ({
                    ...j,
                    title: j.reqCode,
                    value: j.jobId,
                }));
        };

        const handleNumberChange = (e, field) => {
            const val = e.target.value;
            if (Number(val) < 0) return;
            setFuelRequestData((prev) => ({ ...prev, [field]: val }));
        };

        const blockMinusKey = (e) => {
            if (e.key === '-' || e.key === 'e') {
                e.preventDefault();
            }
        };

        return (
            <>
                {isLoading ? (
                    <LoadingSpinner />
                ) : (
                    <form className="ui form" style={{ overflowY: "auto", height: "75vh" }}>
                        {isCreateMode ? (
                            <div className="ui segment">
                                <h4 className="ui deviding header">Fuel Request Information</h4>

                                <SearchDropdown
                                    ref={jobDropdownRef}
                                    label="Request Number"
                                    required={true}
                                    api={searchJobsAPI}
                                    returnFullObject={true}
                                    onResultSelect={(result) => {
                                        const picked = result;

                                        if (picked && !frApprovalEnableList.includes(picked.jobCurrentStatus)) {
                                            toast.error(
                                                "Selected job's current status is not eligible for a fuel request.",
                                                { autoClose: 5000, containerId: "Main" }
                                            );
                                            jobDropdownRef.current?.reset();
                                            return;
                                        }

                                        setFuelRequestData((prev) => ({
                                            ...prev,
                                            frqJob: picked?.jobId ?? "",
                                            frqVehicle: picked?.vehicleId ?? "",
                                            frqVehicleNo: picked?.vehicleNo ?? "",
                                            frqTransporter: picked?.transporter ?? "",
                                            frqTransporterName: picked?.transporterName ?? "",
                                            frqTripDistance: picked?.tripDistanceKm ?? "",
                                        }));

                                        setCurrentErrors((prev) => {
                                            const { frqJob: _, frqVehicle: __, ...rest } = prev;
                                            return rest;
                                        });
                                    }}
                                    error={currentErrors.frqJob}
                                    resetError={() =>
                                        setCurrentErrors((prev) => {
                                            const { frqJob: _, ...rest } = prev;
                                            return rest;
                                        })
                                    }
                                />

                                <div className="three fields">
                                    <Input
                                        label="Vehicle"
                                        placeholder="Auto-filled from Job"
                                        module={MODULES.Transaction}
                                        form={FORM}
                                        field="frq_vehicle"
                                        editMode={false}
                                        error={currentErrors.frqVehicle}
                                        value={fuelRequestData.frqVehicleNo}
                                        disabled={true}
                                    />
                                    <Input
                                        label="Transporter"
                                        placeholder="Auto-filled from Job"
                                        module={MODULES.Transaction}
                                        form={FORM}
                                        field="frq_transporter"
                                        editMode={false}
                                        value={fuelRequestData.frqTransporterName}
                                        disabled={true}
                                    />
                                    <Input
                                        required={true}
                                        label="Trip Distance (km)"
                                        placeholder="Enter Trip Distance"
                                        type="number"
                                        module={MODULES.Transaction}
                                        form={FORM}
                                        field="frq_trip_distance"
                                        editMode={false}
                                        error={currentErrors.frqTripDistance}
                                        resetError={() =>
                                            setCurrentErrors((prev) => {
                                                const { frqTripDistance: _, ...rest } = prev;
                                                return rest;
                                            })
                                        }
                                        value={fuelRequestData.frqTripDistance}
                                        onChange={(e) => handleNumberChange(e, "frqTripDistance")}
                                        onKeyDown={blockMinusKey}
                                        min="0"
                                    />
                                </div>

                                <div className="three fields">
                                    <Input
                                        required={true}
                                        label="Card No"
                                        placeholder="Enter Card No"
                                        type="number"
                                        module={MODULES.Transaction}
                                        form={FORM}
                                        field="frq_card_no"
                                        editMode={false}
                                        error={currentErrors.frqCardNo}
                                        resetError={() =>
                                            setCurrentErrors((prev) => {
                                                const { frqCardNo: _, ...rest } = prev;
                                                return rest;
                                            })
                                        }
                                        value={fuelRequestData.frqCardNo}
                                        onChange={(e) => handleNumberChange(e, "frqCardNo")}
                                        onKeyDown={blockMinusKey}
                                        min="0"
                                    />
                                    <Input
                                        required={true}
                                        label="Requested Fuel (L)"
                                        placeholder="Enter Fuel Amount"
                                        type="number"
                                        module={MODULES.Transaction}
                                        form={FORM}
                                        field="frq_requested_amount"
                                        editMode={false}
                                        error={currentErrors.frqRequestedAmount}
                                        resetError={() =>
                                            setCurrentErrors((prev) => {
                                                const { frqRequestedAmount: _, ...rest } = prev;
                                                return rest;
                                            })
                                        }
                                        value={fuelRequestData.frqRequestedAmount}
                                        onChange={(e) => handleNumberChange(e, "frqRequestedAmount")}
                                        onKeyDown={blockMinusKey}
                                        min="0"
                                    />
                                    <Input
                                        required={true}
                                        label="Required Date"
                                        type="date"
                                        module={MODULES.Transaction}
                                        form={FORM}
                                        field="frq_required_date"
                                        editMode={false}
                                        error={currentErrors.frqRequiredDate}
                                        resetError={() =>
                                            setCurrentErrors((prev) => {
                                                const { frqRequiredDate: _, ...rest } = prev;
                                                return rest;
                                            })
                                        }
                                        value={fuelRequestData.frqRequiredDate}
                                        onChange={(e) =>
                                            setFuelRequestData((prev) => ({ ...prev, frqRequiredDate: e.target.value }))
                                        }
                                        min={new Date().toISOString().split("T")[0]}
                                    />
                                </div>

                                    <Input
                                        label="Description"
                                        placeholder="Enter request description (optional)"
                                        module={MODULES.Transaction}
                                        form={FORM}
                                        field="frq_description"
                                        editMode={false}
                                        value={fuelRequestData.frqDescription}
                                        onChange={(e) =>
                                            setFuelRequestData((prev) => ({ ...prev, frqDescription: e.target.value }))
                                        }
                                    />
                                <div className="ui message" style={{ fontSize: "0.9em" }}>
                                    Statuses are system controlled and will be set to Pending upon submission.
                                </div>
                            </div>
                        ) : (
                            <>
                                <InfoList
                                    title="Fuel Request Details"
                                    content={[
                                        { title: "Request Number", value: fuelRequestData.frqJobReference || "N/A" },
                                        { title: "Requested By", value: fuelRequestData.frqRequestedBy || "N/A" },
                                        { title: "Transporter", value: fuelRequestData.frqTransporterName || "N/A" },
                                        { title: "Trip Distance (km)", value: fuelRequestData.frqTripDistance ?? "N/A" },
                                        { title: "Card No", value: fuelRequestData.frqCardNo || "N/A" },
                                        {
                                            title: "Required Date",
                                            value: fuelRequestData.frqRequiredDate
                                                ? formatFromJsonDate(fuelRequestData.frqRequiredDate).split(" ")[0]
                                                : "N/A"
                                        },
                                        { title: "Requested Amount (L)", value: fuelRequestData.frqRequestedAmount ?? "N/A" },
                                        { title: "Description", value: fuelRequestData.frqDescription || "N/A" },
                                        {
                                            title: "Request Status",
                                            value: <StatusCellStyle value={statusById[fuelRequestData?.frqRequestStatus]} />
                                        },
                                        {
                                            title: "Top Up Status",
                                            value: <StatusCellStyle value={statusById[fuelRequestData?.frqTopUpStatus]} />
                                        }
                                    ]}
                                    Icon={Droplet}
                                />

                                {(isApproveMode || fuelRequestData.frqApprovedAmount) && (
                                    <div className="ui segment">
                                        <h4 className="ui deviding header">Approver Review</h4>
                                        <div className="two fields">
                                            <Input
                                                required={isApproveMode}
                                                label="Approved Amount (L)"
                                                type="number"
                                                module={MODULES.Transaction}
                                                form={FORMS.FUEL_REQUEST.APPROVE.form}
                                                field="frq_approved_amount"
                                                editMode={true}
                                                error={currentErrors.frqApprovedAmount}
                                                resetError={() =>
                                                    setCurrentErrors((prev) => {
                                                        const { frqApprovedAmount: _, ...rest } = prev;
                                                        return rest;
                                                    })
                                                }
                                                value={fuelRequestData.frqApprovedAmount}
                                                onChange={(e) => handleNumberChange(e, "frqApprovedAmount")}
                                                onKeyDown={blockMinusKey}
                                                disabled={!isApproveMode || isRejecting}
                                            />

                                            {isRejecting && isApproveMode ? (
                                                <Input
                                                    key="approve-reject-remarks"
                                                    required={true}
                                                    label="Reject Remarks"
                                                    module={MODULES.Transaction}
                                                    form={FORMS.FUEL_REQUEST.APPROVE.form}
                                                    field="frq_reject_remarks"
                                                    editMode={true}
                                                    error={currentErrors.frqRejectRemarks}
                                                    resetError={() =>
                                                        setCurrentErrors((prev) => {
                                                            const { frqRejectRemarks: _, ...rest } = prev;
                                                            return rest;
                                                        })
                                                    }
                                                    value={fuelRequestData.frqRejectRemarks}
                                                    onChange={(e) =>
                                                        setFuelRequestData((prev) => ({ ...prev, frqRejectRemarks: e.target.value }))
                                                    }
                                                />
                                            ) : (
                                                <Input
                                                    key="approve-approver-remarks"
                                                    label="Approver Remarks"
                                                    module={MODULES.Transaction}
                                                    form={FORMS.FUEL_REQUEST.APPROVE.form}
                                                    field="frq_approver_remarks"
                                                    editMode={true}
                                                    value={fuelRequestData.frqApproverRemarks}
                                                    onChange={(e) =>
                                                        setFuelRequestData((prev) => ({ ...prev, frqApproverRemarks: e.target.value }))
                                                    }
                                                    disabled={!isApproveMode}
                                                />
                                            )}
                                        </div>
                                    </div>
                                )}

                                {(isPumperMode || fuelRequestData.frqTopUpAmount) && (
                                    <div className="ui segment">
                                        <h4 className="ui deviding header">Pumper Verification</h4>
                                        <div className="two fields">
                                            <Input
                                                required={isPumperMode}
                                                label="Top Up Amount (L)"
                                                type="number"
                                                module={MODULES.Transaction}
                                                form={FORMS.FUEL_REQUEST.PUMPER.form}
                                                field="frq_topup_amount"
                                                editMode={true}
                                                error={currentErrors.frqTopUpAmount}
                                                resetError={() =>
                                                    setCurrentErrors((prev) => {
                                                        const { frqTopUpAmount: _, ...rest } = prev;
                                                        return rest;
                                                    })
                                                }
                                                value={fuelRequestData.frqTopUpAmount}
                                                onChange={(e) => handleNumberChange(e, "frqTopUpAmount")}
                                                onKeyDown={blockMinusKey}
                                                disabled={!isPumperMode || isRejecting}
                                            />

                                            {isRejecting && isPumperMode ? (
                                                <Input
                                                    key="pumper-reject-remarks"
                                                    required={true}
                                                    label="Reject Remarks"
                                                    module={MODULES.Transaction}
                                                    form={FORMS.FUEL_REQUEST.PUMPER.form}
                                                    field="frq_reject_remarks"
                                                    editMode={true}
                                                    error={currentErrors.frqRejectRemarks}
                                                    resetError={() =>
                                                        setCurrentErrors((prev) => {
                                                            const { frqRejectRemarks: _, ...rest } = prev;
                                                            return rest;
                                                        })
                                                    }
                                                    value={fuelRequestData.frqRejectRemarks}
                                                    onChange={(e) =>
                                                        setFuelRequestData((prev) => ({ ...prev, frqRejectRemarks: e.target.value }))
                                                    }
                                                />
                                            ) : (
                                                <Input
                                                    key="pumper-pumper-remarks"
                                                    label="Pumper Remarks"
                                                    module={MODULES.Transaction}
                                                    form={FORMS.FUEL_REQUEST.PUMPER.form}
                                                    field="frq_pumper_remarks"
                                                    editMode={true}
                                                    value={fuelRequestData.frqPumperRemarks}
                                                    onChange={(e) =>
                                                        setFuelRequestData((prev) => ({ ...prev, frqPumperRemarks: e.target.value }))
                                                    }
                                                    disabled={!isPumperMode}
                                                />
                                            )}
                                        </div>
                                    </div>
                                )}

                                {!isApproveMode && !isPumperMode && (() => {
                                    const requestStatusLabel = statusById[fuelRequestData?.frqRequestStatus]?.name
                                        ?? statusById[fuelRequestData?.frqRequestStatus];
                                    const topUpStatusLabel = statusById[fuelRequestData?.frqTopUpStatus]?.name
                                        ?? statusById[fuelRequestData?.frqTopUpStatus];

                                    const rejectedByApprover = requestStatusLabel === "Rejected";
                                    const rejectedByPumper = topUpStatusLabel === "Rejected";
                                    const isRejected = rejectedByApprover || rejectedByPumper;

                                    const rejectStageLabel = rejectedByApprover ? "Approver" : "Pumper";

                                    return (
                                        <InfoList
                                            title="Processing Information"
                                            content={
                                                isRejected
                                                    ? [
                                                        {
                                                            title: `Rejected By (${rejectStageLabel})`,
                                                            value: fuelRequestData.frqRejectedBy || fuelRequestData.frqProcessedBy || "N/A",
                                                        },
                                                        {
                                                            title: "Rejected Time",
                                                            value: fuelRequestData.frqRejectedDate
                                                                ? formatFromJsonDate(fuelRequestData.frqRejectedDate)
                                                                : fuelRequestData.frqCompletedDate
                                                                    ? formatFromJsonDate(fuelRequestData.frqCompletedDate)
                                                                    : "N/A",
                                                        },
                                                        {
                                                            title: `${rejectStageLabel} Reject Remarks`,
                                                            value: fuelRequestData.frqRejectRemarks || "N/A",
                                                        },
                                                    ]
                                                    : [
                                                        { title: "Processed By", value: fuelRequestData.frqProcessedBy || "N/A" },
                                                        {
                                                            title: "Completed Time",
                                                            value: fuelRequestData.frqCompletedDate
                                                                ? formatFromJsonDate(fuelRequestData.frqCompletedDate)
                                                                : "N/A",
                                                        },
                                                    ]
                                            }
                                            Icon={Droplet}
                                        />
                                    );
                                })()}
                            </>
                        )}
                    </form>
                )}

            </>
        );
    }
);

export default FuelRequestComponent;